import { action, computed, observable } from 'mobx';
import FastPriorityQueue from 'fastpriorityqueue';
type Loader = () => Promise<any>;

export enum TrialSections {
    CONCEPT = 'concept',
    ABOUT = 'about',
    COURSE = 'course',
    MERIT = 'merit',
    PROCESS = 'process',
    SUPPORT = 'support',
    VOICE = 'voice',
    SCENES = 'scenes',
    LESSONS = 'lessons',
    LESSON_RESERVATION = 'lesson_reservation',
    LESSON_RESERVATION_COMPLETE = 'lesson_reservation_complete',
    RESCHEDULE_RESERVATION = 'reschedule_reservation',
    RESCHEDULE_RESERVATION_COMPLETE = 'reschedule_reservation_complete',
    PLANS = 'plans',
    MAP = 'map',
    FAQ = 'faq',
    FREE_CONSULTATION = 'free-consultation',
    FOOTER = 'footer',
}

export function loadImage(src: string) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = (e) => reject(e);
        image.src = src;
    });
}

export function imageLoader(srcs: string[]) {
    return () => Promise.all(srcs.map(loadImage));
}

class LoaderSpec {
    sectionId: string;
    loader: Loader;
    priority: number;
    resolver: (v: any) => any;
    rejecter: (v: any) => any;

    constructor(sectionId: string, loader: Loader, priority: number) {
        this.sectionId = sectionId;
        this.loader = loader;
        this.priority = priority;
    }

    async load() {
        try {
            const value = await this.loader();
            this.resolver(value);
        } catch (e) {
            this.rejecter(e);
        }
    }
}

class SectionResourceState {
    parent: TrialResourceState;
    sectionId: string;
    constructor(parent: TrialResourceState, sectionId: string) {
        this.parent = parent;
        this.sectionId = sectionId;
    }

    @action.bound
    request(loader: Loader): SectionResourceState {
        this.parent.request(this.sectionId, loader);
        return this;
    }
}

const MAX_WORKERS = 5;

export class TrialResourceState {
    @observable started = false;
    priorityFunc = null;
    loaderQueue = new FastPriorityQueue<LoaderSpec>((x, y) => x.priority < y.priority);
    nextWorkerId = 0;
    workers = {};
    @observable sectionIdToPromises = observable.map();
    @observable sectionIdToRenderStatus = observable.map();
    @observable sectionIdToCompletionStatus = observable.map();

    constructor(priorityFunc: (sectionId: string) => number = () => 9999) {
        this.priorityFunc = priorityFunc;
    }

    @action.bound
    request(sectionId: string, loader: Loader) {
        this.enqueueLoader(sectionId, loader);
        if (this.started && this.workerCount < MAX_WORKERS) {
            this.startWorker();
        }
    }

    @action.bound
    reportRender(sectionId) {
        this.sectionIdToRenderStatus.set(sectionId, true);
    }

    private enqueueLoader(sectionId: string, loader: Loader) {
        const loaderSpec = new LoaderSpec(sectionId, loader, this.priorityFunc(sectionId));
        this.loaderQueue.add(loaderSpec);
        const promise = new Promise((resolve, reject) => {
            loaderSpec.resolver = resolve;
            loaderSpec.rejecter = reject;
        });
        this.sectionIdToPromises.set(sectionId, (this.sectionIdToPromises.get(sectionId) || []).concat([promise]));
    }

    ofSection(sectionId, operator: (state: SectionResourceState) => any) {
        operator(new SectionResourceState(this, sectionId));
    }

    @action.bound
    start() {
        this.started = true;
        [...this.sectionIdToPromises.keys()].forEach((key) => this.updateCompletedStatus(key));
        if (this.workerCount < MAX_WORKERS) {
            [...new Array(MAX_WORKERS - this.workerCount)].forEach(this.startWorker);
        }
    }

    @computed
    get hasNext() {
        return !this.loaderQueue.isEmpty();
    }

    get workerCount() {
        return Object.keys(this.workers).length;
    }

    @action.bound
    async startWorker() {
        if (this.loaderQueue.isEmpty()) {
            return;
        }
        this.workers[this.nextWorkerId++] = true;
        const nextLoader = this.loaderQueue.poll();
        await nextLoader.load();
        this.updateCompletedStatus(nextLoader.sectionId);
        delete this.workers[this.nextWorkerId];
        if (this.hasNext) {
            await this.startWorker();
        }
    }

    @action.bound
    getCompleteHandler(sectionId: string): Promise<any> {
        if (!this.sectionIdToPromises.has(sectionId)) {
            return Promise.resolve();
        }
        return Promise.all(this.sectionIdToPromises.get(sectionId));
    }

    @action.bound
    getAllCompletionHandler(): Promise<any> {
        const values = [...this.sectionIdToPromises.values()].reduce((arr, item) => arr.concat([...item]), []);
        return Promise.all(values);
    }

    updateCompletedStatus(sectionId) {
        const completionHandler = this.getCompleteHandler(sectionId);
        if (!completionHandler) {
            this.sectionIdToCompletionStatus.set(sectionId, true);
        }
        completionHandler
            .then(() => {
                this.sectionIdToCompletionStatus.set(sectionId, true);
            })
            .catch(() => {
                this.sectionIdToCompletionStatus.set(sectionId, true);
            });
    }

    getDependentSectionIds(sectionId: string) {
        const basePriority = this.priorityFunc(sectionId);
        return [...this.sectionIdToPromises.keys()].filter((key) => {
            const priority = this.priorityFunc(key);
            return priority < basePriority;
        });
    }

    isAllCompleted(sectionIds: string[] = this.getAllSectionIds()): boolean {
        return sectionIds.every((key) => this.isCompleted(key));
    }

    @computed
    get allCompleted() {
        return this.isAllCompleted();
    }

    isDependentSectionCompleted(sectionId): boolean {
        return this.isAllCompleted(this.getDependentSectionIds(sectionId));
    }

    isCompleted(sectionId: string): boolean {
        if (!this.started) {
            return false;
        }
        if (!this.sectionIdToPromises.has(sectionId)) {
            return true;
        }
        return this.sectionIdToCompletionStatus.get(sectionId) || false;
    }

    isRendered(sectionId: string): boolean {
        if (!this.started) {
            return false;
        }
        return this.sectionIdToRenderStatus.get(sectionId) || false;
    }

    getAllSectionIds() {
        return [...this.sectionIdToPromises.keys()];
    }

    isAllRendered(sectionIds: string[] = this.getAllSectionIds()): boolean {
        if (!this.started) {
            return false;
        }
        return sectionIds.every((sectionId) => this.isRendered(sectionId));
    }

    isDependentSectionRendered(sectionId): boolean {
        return this.isAllRendered(this.getDependentSectionIds(sectionId));
    }
}
